"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, Calendar, ShieldCheck, Star, Search, Building2, Phone, Mail } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  title: "Direct Consultant Access",
  desc: "You deal exclusively with me, Yassine Essaadi—no account managers or juniors."
}, {
  title: "Ipswich & Suffolk Focused",
  desc: "Tailored Google Maps & local search strategies to dominate regional searches."
}, {
  title: "Transparent & Deliverable-Led",
  desc: "Clear monthly insights, actionable technical fixes, and measurable local growth."
}];
const localAreas = ["Ipswich Town Centre", "Waterfront", "Kesgrave", "Martlesham", "Felixstowe", "Woodbridge"];
export default function Localconsultanthero() {
  return <section id="localconsultanthero" data-nav="dark" className="relative min-h-[85vh] w-full overflow-hidden bg-primary text-primary-foreground pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background glowing accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 size-96 rounded-full bg-accent/20 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 size-[500px] rounded-full bg-accent/15 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Hero Narrative */}
          <motion.div className="lg:col-span-7 flex flex-col space-y-6 text-left" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }}>
            {/* Trust and Location Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-accent/20 border-accent/40 text-primary-foreground px-3.5 py-1 text-xs font-semibold tracking-wide uppercase rounded-full gap-1.5 backdrop-blur-sm">
                <MapPin className="size-3.5 text-accent" />
                Ipswich & Suffolk Local SEO
              </Badge>
              <Badge variant="outline" className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground px-3 py-1 text-xs font-medium rounded-full gap-1">
                <ShieldCheck className="size-3.5 text-accent" />
                100% Freelance Expert
              </Badge>
            </div>

            {/* H1 Heading */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15]">
              Local SEO Consultant in Ipswich
            </h1>

            {/* Subtitle / Intro Narrative */}
            <p className="font-sans text-base sm:text-lg md:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
              I help local businesses across Ipswich and Suffolk dominate Google Maps, attract high-intent local customers, and convert search traffic into real revenue. You work directly with me—Yassine Essaadi—combining hands-on technical SEO expertise with practical local search strategy.
            </p>

            {/* Value Props Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {trustHighlights.map((item, i) => <div key={i} data-index={i} className="bg-primary-foreground/10 border border-primary-foreground/15 rounded-xl p-3.5 backdrop-blur-sm flex flex-col space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold text-xs text-primary-foreground">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs text-primary-foreground/80 leading-snug">
                    {item.desc}
                  </p>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg font-semibold text-base h-12 px-8 rounded-lg transition-all">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <SafeHydrate><Calendar className="ml-2 size-5" /></SafeHydrate>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-semibold text-base h-12 px-7 rounded-lg backdrop-blur-sm">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
            </div>

            {/* Direct Contact Quick Line */}
            <div className="pt-4 border-t border-primary-foreground/15 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-primary-foreground/80">
              <span className="flex items-center gap-1.5 font-medium">
                <Mail className="size-3.5 text-accent" />
                <a href="mailto:contact@ye-consultancy.com" className="hover:underline text-primary-foreground">
                  contact@ye-consultancy.com
                </a>
              </span>
              <span className="flex items-center gap-1.5 font-medium">
                <Phone className="size-3.5 text-accent" />
                <a href="tel:+447490984430" className="hover:underline text-primary-foreground">
                  +44 7490984430
                </a>
              </span>
            </div>

          </motion.div>

          {/* Right Column: Expert Profile & Local Focus Card */}
          <motion.div className="lg:col-span-5 relative" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }}>
            <div className="relative mx-auto max-w-md lg:max-w-none rounded-2xl bg-primary-foreground/10 border border-primary-foreground/20 p-6 shadow-2xl backdrop-blur-md">
              
              {/* Profile Image with Overlay Badge */}
              <div className="relative rounded-xl overflow-hidden aspect-4/5 bg-primary/40 border border-primary-foreground/20 mb-6">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Independent Local SEO Consultant in Ipswich" className="w-full h-full object-cover object-center" loading="lazy" />
                
                <div className="absolute bottom-3 left-3 right-3 bg-primary/90 border border-primary-foreground/20 p-3 rounded-lg backdrop-blur-md flex items-center justify-between">
                  <div>
                    <p className="font-serif font-bold text-sm text-primary-foreground">Yassine Essaadi</p>
                    <p className="text-xs text-primary-foreground/80">Independent SEO Strategist</p>
                  </div>
                  <Badge className="bg-accent text-accent-foreground font-semibold text-xs px-2.5 py-0.5">
                    No Agency Middlemen
                  </Badge>
                </div>
              </div>

              {/* Local Search Highlights */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs font-semibold text-primary-foreground">
                  <span className="flex items-center gap-1.5">
                    <Search className="size-4 text-accent" />
                    Google Local Pack & Maps Strategy
                  </span>
                  <div className="flex text-amber-300">
                    <Star className="size-3.5 fill-current" />
                    <Star className="size-3.5 fill-current" />
                    <Star className="size-3.5 fill-current" />
                    <Star className="size-3.5 fill-current" />
                    <Star className="size-3.5 fill-current" />
                  </div>
                </div>

                <div className="bg-primary/50 border border-primary-foreground/15 rounded-lg p-3 text-xs text-primary-foreground/90 space-y-2">
                  <p className="font-medium text-primary-foreground">
                    Ipswich & Suffolk Coverage Areas:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {localAreas.map((area, idx) => <span key={idx} data-index={idx} className="bg-primary-foreground/10 border border-primary-foreground/15 text-primary-foreground rounded-md px-2 py-0.5 text-[13px]">
                        {area}
                      </span>)}
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-between text-xs text-primary-foreground/80">
                  <span className="flex items-center gap-1">
                    <Building2 className="size-3.5 text-accent" />
                    Tailored B2B & Local Services SEO
                  </span>
                  <Link to="/LocalSEOPage" className="text-accent hover:underline font-medium">
                    Local SEO Details →
                  </Link>
                </div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}